import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { axiosErrorHandler } from 'common/helpers/axios.helper';
import { AppDispatch, RootState } from 'app/store';
import {
  IAccount,
  IBlockAccountPayload,
  IAccountBrand,
  IAccountBrandsParams,
  IUpdateAccountBrandsPayload,
  ICreateAccountPayload,
  IDeleteAccountPayload,
  IAccountListParams,
  IUnblockAccountPayload,
  IUpdateAccountPayload,
  IUpdateSellerAccountPayload,
} from 'entities/Account/Account.models';
import { accountTransport } from 'entities/Account/Account.transport';
import { patchCurrentUserAccountName } from 'entities/User/User.slice';

interface IState {
  accountList: IAccount[];
  accountCount: number;
  account: IAccount | null;
  isAccountLoading: boolean;
  accountError: string | null;
  accountBrands: IAccountBrand[];
}

const initialState: IState = {
  accountList: [],
  accountCount: 0,
  account: null,
  isAccountLoading: false,
  accountError: null,
  accountBrands: [],
};

export const accountSlice = createSlice({
  name: 'account',
  initialState,
  reducers: {
    setAccountList(state, action: PayloadAction<IAccount[]>) {
      state.accountList = action.payload;
    },
    setAccountCount(state, action: PayloadAction<number>) {
      state.accountCount = action.payload;
    },
    setAccount(state, action: PayloadAction<IAccount>) {
      state.account = action.payload;
    },
    setIsAccountLoading(state, action: PayloadAction<boolean>) {
      state.isAccountLoading = action.payload;
    },
    setAccountError(state, action: PayloadAction<string | null>) {
      state.accountError = action.payload;
    },
    setAccountBrands(state, action: PayloadAction<IAccountBrand[]>) {
      state.accountBrands = action.payload;
    },
    addAccountsItem(state, action: PayloadAction<IAccount>) {
      state.accountList.push(action.payload);
    },
    patchAccountsItem(state, action: PayloadAction<IAccount>) {
      const account = state.accountList.find((item) => item.id === action.payload.id);

      if (!account) {
        return;
      }

      Object.assign(account, action.payload);
    },
    removeAccountsItem(state, action: PayloadAction<number>) {
      state.accountList = state.accountList.filter((item) => item.id !== action.payload);
    },
    patchAccountBrandItems(state, action: PayloadAction<IAccountBrand[]>) {
      const accountBrandsMap = new Map(action.payload.map((item) => [item.id, item]));

      state.accountBrands = state.accountBrands.map((item) => accountBrandsMap.get(item.id) ?? item);
    },
  },
});

export const {
  setAccountList,
  setAccountCount,
  setAccount,
  setIsAccountLoading,
  setAccountError,
  setAccountBrands,
  addAccountsItem,
  patchAccountsItem,
  removeAccountsItem,
  patchAccountBrandItems,
} = accountSlice.actions;

export const selectAccount = (state: RootState) => state.account.account;
export const selectAccountList = (state: RootState) => state.account.accountList;
export const selectAccountCount = (state: RootState) => state.account.accountCount;
export const selectIsAccountLoading = (state: RootState) => state.account.isAccountLoading;
export const selectAccountError = (state: RootState) => state.account.accountError;
export const selectAccountBrands = (state: RootState) => state.account.accountBrands;

export const accountReducer = accountSlice.reducer;

export const getAccounts = (params: IAccountListParams) => async (dispatch: AppDispatch) => {
  dispatch(setIsAccountLoading(true));

  try {
    const response = await accountTransport.get(params);
    dispatch(setAccountList(response.data));
    dispatch(setAccountCount(response.count));
  } catch (error) {
    axiosErrorHandler(error);
  } finally {
    dispatch(setIsAccountLoading(false));
  }
};

export const getAccountById = (id: number) => async (dispatch: AppDispatch) => {
  dispatch(setIsAccountLoading(true));

  try {
    const response = await accountTransport.getById(id);
    dispatch(setAccount(response));
  } catch (error) {
    axiosErrorHandler(error);
  } finally {
    dispatch(setIsAccountLoading(false));
  }
};

export const createAccount = (payload: ICreateAccountPayload) => async (dispatch: AppDispatch) => {
  dispatch(setIsAccountLoading(true));

  try {
    const response = await accountTransport.create(payload);
    dispatch(addAccountsItem(response));
    payload.onSuccess?.();
  } catch (error) {
    axiosErrorHandler(error, setAccountError);
  } finally {
    dispatch(setIsAccountLoading(false));
  }
};

export const updateAccount = (payload: IUpdateAccountPayload) => async (dispatch: AppDispatch) => {
  dispatch(setIsAccountLoading(true));

  try {
    const response = await accountTransport.update(payload);
    dispatch(patchAccountsItem(response));
    dispatch(setAccount(response));
    dispatch(patchCurrentUserAccountName(response.name));
    payload.onSuccess?.();
  } catch (error) {
    axiosErrorHandler(error);
  } finally {
    dispatch(setIsAccountLoading(false));
  }
};

export const blockAccount = (payload: IBlockAccountPayload) => async (dispatch: AppDispatch) => {
  dispatch(setIsAccountLoading(true));

  try {
    const response = await accountTransport.block(payload);
    dispatch(patchAccountsItem(response));
    payload.onSuccess?.();
  } catch (error) {
    axiosErrorHandler(error);
  } finally {
    dispatch(setIsAccountLoading(false));
  }
};

export const unblockAccount = (payload: IUnblockAccountPayload) => async (dispatch: AppDispatch) => {
  dispatch(setIsAccountLoading(true));

  try {
    const response = await accountTransport.unblock(payload);
    dispatch(patchAccountsItem(response));
    payload.onSuccess?.();
  } catch (error) {
    axiosErrorHandler(error);
  } finally {
    dispatch(setIsAccountLoading(false));
  }
};

export const deleteAccount = (payload: IDeleteAccountPayload) => async (dispatch: AppDispatch) => {
  dispatch(setIsAccountLoading(true));

  try {
    const response = await accountTransport.delete(payload);
    dispatch(removeAccountsItem(response.id));
    payload.onSuccess?.();
  } catch (error) {
    axiosErrorHandler(error);
  } finally {
    dispatch(setIsAccountLoading(false));
  }
};

export const updateSellerAccount = (payload: IUpdateSellerAccountPayload) => async (dispatch: AppDispatch) => {
  dispatch(setIsAccountLoading(true));

  try {
    const response = await accountTransport.updateSellerAccount(payload);
    dispatch(patchAccountsItem(response));
    payload.onSuccess?.();
  } catch (error) {
    axiosErrorHandler(error);
  } finally {
    dispatch(setIsAccountLoading(false));
  }
};

export const generateAccountWebhookSign = () => async (dispatch: AppDispatch) => {
  dispatch(setIsAccountLoading(true));

  try {
    const response = await accountTransport.generateWebhookSign();
    dispatch(setAccount(response));
  } catch (error) {
    axiosErrorHandler(error);
  } finally {
    dispatch(setIsAccountLoading(false));
  }
};

export const getAccountBrands = (params: IAccountBrandsParams) => async (dispatch: AppDispatch) => {
  dispatch(setIsAccountLoading(true));

  try {
    const response = await accountTransport.getBrands(params);
    dispatch(setAccountBrands(response.data));
  } catch (error) {
    axiosErrorHandler(error);
  } finally {
    dispatch(setIsAccountLoading(false));
  }
};

export const updateAccountBrands = (payload: IUpdateAccountBrandsPayload) => async (dispatch: AppDispatch) => {
  dispatch(setIsAccountLoading(true));

  try {
    const response = await accountTransport.updateBrands(payload);
    dispatch(patchAccountBrandItems(response.data));
    payload.onSuccess?.();
  } catch (error) {
    axiosErrorHandler(error);
  } finally {
    dispatch(setIsAccountLoading(false));
  }
};
